/**
 * EvolveRPG world configuration — the `WorldConfig` for
 * evolverpg.com. The ONE place this world's facets + access policy
 * are declared; the shared shell reads it via the active-config singleton.
 *
 * EvolveRPG is a fast-play, pick-up-and-play, multi-genre tabletop RPG for
 * one-shots and short campaigns; characters evolve rapidly within a session.
 */
import type { WorldConfig } from '@gamecodex/world-config'
// Shared shell baseline (single locale: English) + EvolveRPG's own fast-play,
// momentum-driven voice overrides — see src/content/en.ts for the full set.
import { baseEn } from '@/content/en'
import { evoEn } from './content/en'

export const EVOLVERPG_SYSTEM_ID = 'evolverpg-core'

// Eternal City: the living-city simulation, hosted by the eternal-city Worker (packages/@gamecodex/eternal-city-worker).
// A public URL, deliberately a constant and not a build-time env var: Netlify's secret scanner fails builds when an env
// value shows up in the output. `VITE_ETERNAL_CITY_URL` may override it for local development.
const ETERNAL_CITY_URL = (import.meta.env.VITE_ETERNAL_CITY_URL as string | undefined) || 'https://city.gamecodex.com'

export const evolverpgWorldConfig: WorldConfig = {
  worldId: 'evolverpg',
  worldName: 'EvolveRPG',
  legalContentName: 'EvolveRPG Ruleset',
  domain: 'evolverpg.com',

  locales: {
    default: 'en',
    supported: [
      { code: 'en', label: 'English', native: 'English', dir: 'ltr' },
    ],
  },

  content: {
    en: { 'common.worldName': 'EvolveRPG', ...baseEn, ...evoEn },
  },

  // characters are managed in the game (Jared, 11/2): signing in drops you into the city, and so do your avatar and name
  playerHome: '/world/cities/montkelian',

  branding: {
    images: {
      // The painted EVOLVE RPG wordmark (Jared, 11/2), cut from the game header kit (public/brand).
      logo: '/brand/evolverpg-logo-gold.webp',
      ogImage: '/og/default.jpg',
      favicon: '/favicon.png',
      avatarFallback: '/avatars/montkelian/sorendal-female.webp',
      parchmentTexture: '/textures/parchment-dark.png',
      splatterTexture: '/textures/blood-splatter.png',
    },
    fonts: {
      display: 'Grenze',
      heading: 'Grenze',
      body: 'Alegreya Sans',
      stylesheetUrl:
        'https://fonts.googleapis.com/css2?family=Grenze:wght@500;600;700&family=Alegreya+Sans:wght@400;500;700&family=Inter:wght@400;500;600&display=swap',
    },
    // the painted game header over the city game (Jared, 11/2: it belongs to the game): sliced kit art in public/brand/header
    headerKit: '/brand/header',
    background: { variant: 'evolve-forge', params: { particleColor: '#e0a040', fogColor: '#0b0e12' } },
  },

  // In-world starting headshots (Montkelian townsfolk, from the setting's
  // reference bible archetypes). Replaces the generated robot placeholder.
  startingAvatars: {
    // Picker UI copy (heading, hint, "All" chip) lives in content/en/dashboard.ts.
    // Lineage names, groups and blurbs below are setting CONTENT: in-world voice
    // and spelling, not Americanized UI copy.
    lineages: [
      { id: 'sorendal', group: 'Peoples of Montkelian', label: 'Sorendal', blurb: 'Tall, winter-pale and storm-eyed, the noble houses and royal line, in fur and fog-grey wool with antler-beaded braids.' },
      { id: 'kessari', group: 'Peoples of Montkelian', label: 'Kessari', blurb: 'Golden-bronze lineage of traders and travellers.' },
      { id: 'brannoch', group: 'Peoples of Montkelian', label: 'Brannoch', blurb: 'Peat-cutters of the highland moors, in oiled wool, knitted caps and sheepskin.' },
      { id: 'duvenni', group: 'Peoples of Montkelian', label: 'Duvenni', blurb: 'Folk in camwood-red raffia and coiled brass armbands.' },
      { id: 'esharra', group: 'Peoples of Montkelian', label: 'Esharra', blurb: 'People of the salt flats, head-wrapped in undyed white and sand.' },
      { id: 'hallorn', group: 'Peoples of Montkelian', label: 'Hallorn', blurb: 'Mountain herders in loden felt, with iron-shod staffs and brass herd-bells.' },
      { id: 'ilunei', group: 'Peoples of Montkelian', label: 'Ilunei', blurb: 'Snow-coast folk in sealskin and white fox fur, bone snow-goggles on the brow.' },
      { id: 'lioren', group: 'Peoples of Montkelian', label: 'Lioren', blurb: 'River-town scholars in layered silk and hemp, lacquer combs and ink-stained fingers.' },
      { id: 'mirevai', group: 'Peoples of Montkelian', label: 'Mirevai', blurb: 'Island voyagers in bark-cloth, each sea crossing tattooed along the arms.' },
      { id: 'nalori', group: 'Peoples of Montkelian', label: 'Nalori', blurb: 'Coast folk with sun-gold curls, palm-fiber wraps and shell money.' },
      { id: 'pravanni', group: 'Peoples of Montkelian', label: 'Pravanni', blurb: 'Block-printed cotton in turmeric and rose, glass bangles and flowers in the braid.' },
      { id: 'zhurai', group: 'Peoples of Montkelian', label: 'Zhurai', blurb: 'Steppe riders in quilted felt coats, fur-trimmed hats and horsehair tassels.' },
      { id: 'anthocerot', group: 'The Pixen', label: 'Anthocerot', blurb: 'Hornwort-born of the sodden loam; some carry a cold green glow, and some never kindle.' },
      { id: 'bryophyte', group: 'The Pixen', label: 'Bryophyte', blurb: 'Moss-born, slow to anger, low to the ground, and they remember every footfall upon it.' },
      { id: 'pteridophyle', group: 'The Pixen', label: 'Pteridophyle', blurb: 'Fern-born, crowned in fiddleheads that unfurl like slow fists wherever the shade runs deep.' },
      { id: 'ultrich', group: 'The Pixen', label: 'Ultrich', blurb: 'Flower-born of the court, petalled in light and prized above every bloom on the forest floor.' },
    ],
    portraits: [
      { id: 'sorendal-female', lineageId: 'sorendal', label: 'Sorendal woman', url: '/avatars/montkelian/sorendal-female.webp' },
      { id: 'sorendal-male', lineageId: 'sorendal', label: 'Sorendal man', url: '/avatars/montkelian/sorendal-male.webp' },
      { id: 'kessari-female', lineageId: 'kessari', label: 'Kessari woman', url: '/avatars/montkelian/kessari-female.webp' },
      { id: 'kessari-male', lineageId: 'kessari', label: 'Kessari man', url: '/avatars/montkelian/kessari-male.webp' },
      { id: 'brannoch-female', lineageId: 'brannoch', label: 'Brannoch woman', url: '/avatars/montkelian/brannoch-female.webp' },
      { id: 'brannoch-male', lineageId: 'brannoch', label: 'Brannoch man', url: '/avatars/montkelian/brannoch-male.webp' },
      { id: 'duvenni-female', lineageId: 'duvenni', label: 'Duvenni woman', url: '/avatars/montkelian/duvenni-female.webp' },
      { id: 'duvenni-male', lineageId: 'duvenni', label: 'Duvenni man', url: '/avatars/montkelian/duvenni-male.webp' },
      { id: 'esharra-female', lineageId: 'esharra', label: 'Esharra woman', url: '/avatars/montkelian/esharra-female.webp' },
      { id: 'esharra-male', lineageId: 'esharra', label: 'Esharra man', url: '/avatars/montkelian/esharra-male.webp' },
      { id: 'hallorn-female', lineageId: 'hallorn', label: 'Hallorn woman', url: '/avatars/montkelian/hallorn-female.webp' },
      { id: 'hallorn-male', lineageId: 'hallorn', label: 'Hallorn man', url: '/avatars/montkelian/hallorn-male.webp' },
      { id: 'ilunei-female', lineageId: 'ilunei', label: 'Ilunei woman', url: '/avatars/montkelian/ilunei-female.webp' },
      { id: 'ilunei-male', lineageId: 'ilunei', label: 'Ilunei man', url: '/avatars/montkelian/ilunei-male.webp' },
      { id: 'lioren-female', lineageId: 'lioren', label: 'Lioren woman', url: '/avatars/montkelian/lioren-female.webp' },
      { id: 'lioren-male', lineageId: 'lioren', label: 'Lioren man', url: '/avatars/montkelian/lioren-male.webp' },
      { id: 'mirevai-female', lineageId: 'mirevai', label: 'Mirevai woman', url: '/avatars/montkelian/mirevai-female.webp' },
      { id: 'mirevai-male', lineageId: 'mirevai', label: 'Mirevai man', url: '/avatars/montkelian/mirevai-male.webp' },
      { id: 'nalori-female', lineageId: 'nalori', label: 'Nalori woman', url: '/avatars/montkelian/nalori-female.webp' },
      { id: 'nalori-male', lineageId: 'nalori', label: 'Nalori man', url: '/avatars/montkelian/nalori-male.webp' },
      { id: 'pravanni-female', lineageId: 'pravanni', label: 'Pravanni woman', url: '/avatars/montkelian/pravanni-female.webp' },
      { id: 'pravanni-male', lineageId: 'pravanni', label: 'Pravanni man', url: '/avatars/montkelian/pravanni-male.webp' },
      { id: 'zhurai-female', lineageId: 'zhurai', label: 'Zhurai woman', url: '/avatars/montkelian/zhurai-female.webp' },
      { id: 'zhurai-male', lineageId: 'zhurai', label: 'Zhurai man', url: '/avatars/montkelian/zhurai-male.webp' },
      { id: 'anthocerot-female', lineageId: 'anthocerot', label: 'Anthocerot woman', url: '/avatars/pixen/anthocerot-female.webp' },
      { id: 'anthocerot-male', lineageId: 'anthocerot', label: 'Anthocerot man', url: '/avatars/pixen/anthocerot-male.webp' },
      { id: 'bryophyte-female', lineageId: 'bryophyte', label: 'Bryophyte woman', url: '/avatars/pixen/bryophyte-female.webp' },
      { id: 'bryophyte-male', lineageId: 'bryophyte', label: 'Bryophyte man', url: '/avatars/pixen/bryophyte-male.webp' },
      { id: 'pteridophyle-female', lineageId: 'pteridophyle', label: 'Pteridophyle woman', url: '/avatars/pixen/pteridophyle-female.webp' },
      { id: 'pteridophyle-male', lineageId: 'pteridophyle', label: 'Pteridophyle man', url: '/avatars/pixen/pteridophyle-male.webp' },
      { id: 'ultrich-female', lineageId: 'ultrich', label: 'Ultrich woman', url: '/avatars/pixen/ultrich-female.webp' },
      { id: 'ultrich-male', lineageId: 'ultrich', label: 'Ultrich man', url: '/avatars/pixen/ultrich-male.webp' },
    ],
  },

  // EvolveRPG's own starter set (per-world, see ChargenConfig): the city's human-world roles and looks only. The
  // Pixen classes (api 27-29) and the Pixen lineages stay in the api and in startingAvatars above, just not offered;
  // drop the hideGroups entry (and add the ids) to offer them again.
  chargen: {
    roles: { setting: 'montkelian', include: [30, 31, 32, 33, 34] },
    looks: { hideGroups: ['The Pixen'] },
    // Every human rig, each a whole EvolveRPG starting sheet (world-ui vtt/humanStarts.ts), in place of the SRD roll.
    humanStarts: true,
  },

  ...(ETERNAL_CITY_URL
    ? {
        eternalCity: {
          serviceUrl: ETERNAL_CITY_URL,
          renderer: 'three',
          cities: [
            {
              slug: 'montkelian',
              name: 'Montkelian',
              hexId: 'pent-0',
              blurb: 'A grey, rain-heavy capital where everyone keeps a daily routine. Follow them, talk with them, and spend a day beside people whose lives go on without you.',
              cover: '/eternal-city/covers/montkelian.jpg',
              coverPortrait: '/eternal-city/covers/montkelian-portrait.jpg',
            },
          ],
          // Montkelian is EvolveRPG's VTT: character creation lands players in its shared Core campaign.
          vtt: { citySlug: 'montkelian', classSetting: 'montkelian' },
        },
      }
    : {}),

  gameSystems: [
    { id: EVOLVERPG_SYSTEM_ID, label: 'EvolveRPG Core', available: true },
    { id: 'evo-fantasy', label: 'EvolveRPG Fantasy', available: false },
    { id: 'evo-scifi', label: 'EvolveRPG Sci-Fi', available: false },
    { id: 'evo-horror', label: 'EvolveRPG Horror', available: false },
  ],
  settings: [
    { id: 'evolverpg-core', label: 'Core Ruleset', available: true },
    // The city of Montkelian (its places and people): system-agnostic content, so listed across every system.
    { id: 'montkelian', label: 'Montkelian', available: true, anySystem: true },
  ],
  defaultSystem: EVOLVERPG_SYSTEM_ID,

  homeCampaign: {
    slug: 'evolverpg-core-ruleset',
    eyebrow: 'Fast-Play Tabletop RPG',
    title: 'EvolveRPG',
    body: 'A 3D roleplaying game where every character starts at zero and grows through play.',
    ctaLabel: 'Explore EvolveRPG',
  },

  seo: {
    defaultDescription:
      'EvolveRPG is a 3D roleplaying game in your browser where every character starts at zero. Walk Montkelian, a living city, or take a party into The Portal, a turn-based boss battle. Free with an account.',
    tagline: 'Start at zero.',
    defaultOgImage: '/og/default.jpg',
  },

  // Cross-setting books this system site offers on its shelf (still gated by
  // an active Atlas deliverable — see `WorldConfig.extraBookIds`).
  extraBookIds: [
    '4792808e-6ad3-4ae0-96ef-9d372636a7f0', // System Reference Document 5.2.1 — Illustrated Edition
  ],

  openCanonSystemIds: ['evolverpg-core'],
  lockedSettingIds: ['evolverpg-core'],
  unlockBookIds: [],
  fallbackUnlockBookId: '',
}
